import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_PORTAL_DASHBOARD,
  PAGE_ROUTE_LOGIN,
  PAGE_ROUTE_USER_GUIDE,
  PORTAL_PREFIX,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { welcomeAboutSideImage } from "../../assets";
import AuthContext from "../../context/authContext";
import { AUTH_STATUS } from "../../context/enums/authEnums";
import AppButton from "../common/AppButton";
import WelcomeParallax from "./WelcomeParallax";
import {
  welcomeAboutImageSx,
  welcomeSectionBodySx,
  welcomeSectionContentSx,
  welcomeSectionEyebrowSx,
  welcomeSectionSx,
  welcomeSectionTitleSx
} from "./welcomeSectionStyles";

const WelcomeAboutSection: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext);
  const { authContextInfo } = useContext(AuthContext);
  const userGuidePath = localizedPath(
    PAGE_ROUTE_USER_GUIDE,
    language,
    defaultLang
  );

  const handleGetStarted = () => {
    const path =
      authContextInfo.auth.status === AUTH_STATUS.AUTHENTICATED
        ? `${PORTAL_PREFIX}${PAGE_PORTAL_DASHBOARD}`
        : PAGE_ROUTE_LOGIN;
    navigate(localizedPath(path, language, defaultLang));
  };

  return (
    <Box
      component="section"
      data-testid="welcome-about-section"
      sx={welcomeSectionSx}
    >
      <Box sx={welcomeSectionContentSx}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: { xs: 4, md: 6 }
          }}
        >
          <Box sx={{ flex: 1, width: "100%" }}>
            <WelcomeParallax offset={48}>
              <Box sx={welcomeAboutImageSx}>
                <Box
                  component="img"
                  src={welcomeAboutSideImage}
                  alt={t(
                    "welcome-about-image-alt",
                    "Person using Kivunova FMS on a smartphone"
                  )}
                  sx={{
                    width: "100%",
                    maxHeight: 280,
                    height: "auto",
                    objectFit: "cover",
                    display: "block"
                  }}
                />
              </Box>
            </WelcomeParallax>
          </Box>

          <Box
            sx={{
              flex: 1,
              width: "100%",
              textAlign: { xs: "center", md: "left" }
            }}
          >
            <WelcomeParallax offset={44} direction="reverse">
              <Typography component="p" sx={welcomeSectionEyebrowSx}>
                {t("welcome-about-eyebrow", "About Kivunova FMS")}
              </Typography>
              <Typography component="h2" sx={welcomeSectionTitleSx}>
                {t("welcome-about-title", "Know more about this system")}
              </Typography>
              <Typography sx={welcomeSectionBodySx}>
                {t(
                  "welcome-about-body",
                  "Kivunova FMS is a financial and inventory management system that helps businesses manage products, stock, orders, stores, and performance from one secure, multilingual workspace—built for Rwanda and beyond."
                )}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent={{ xs: "center", md: "flex-start" }}
                alignItems="center"
              >
                <AppButton
                  variant="primary"
                  onClick={handleGetStarted}
                  endIcon={<ArrowForwardIcon />}
                  data-testid="welcome-about-get-started"
                >
                  {t("get-started-button", "Get Started")}
                </AppButton>
                <MuiLink
                  component={Link}
                  to={userGuidePath}
                  underline="hover"
                  fontWeight={600}
                  color="primary.main"
                  data-testid="welcome-user-guide-link"
                >
                  {t("welcome-about-user-guide-cta", "User guide")}
                </MuiLink>
              </Stack>
            </WelcomeParallax>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WelcomeAboutSection;
